import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, Heart } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DonateSuccess() {
  const [params] = useSearchParams();
  const donationId = params.get("donation_id") || "";
  const [donation, setDonation] = useState(null); // null = checking, false = not found
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!donationId) {
      setDonation(false);
      return;
    }
    let cancelled = false;
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/donations/${donationId}`);
        if (cancelled) return;
        setDonation(data);
        if (data.status === "PENDING" && attempts < 15) {
          timer = setTimeout(() => setAttempts((a) => a + 1), 2000);
        }
      } catch {
        if (!cancelled) setDonation(false);
      }
    };
    poll();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [donationId, attempts]);

  const paid = donation && donation.status === "PAID";
  const pending = donation && donation.status === "PENDING";

  return (
    <div className="bg-sand grain py-20" data-testid="donate-success-page">
      <div className="mx-auto max-w-lg px-6">
        <Reveal>
          <div className="rounded-[2rem] border border-coal/5 bg-white p-10 text-center shadow-[0_24px_60px_-24px_rgba(26,29,36,0.35)]">
            {paid ? (
              <>
                <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-teal-subtle text-teal">
                  <CheckCircle2 size={32} />
                </span>
                <h1 className="mt-5 font-display text-3xl font-extrabold tracking-tight text-coal" data-testid="donation-paid-heading">
                  Thank you, change maker
                </h1>
                <p className="mt-3 text-sm leading-relaxed text-coal-muted">
                  Your donation of <span className="font-bold text-ember">R{Number(donation.amount).toLocaleString("en-ZA")}</span> has
                  been received. It goes straight to transport, food, books and the experiences that
                  turn potential into opportunity.
                </p>
              </>
            ) : pending ? (
              <>
                <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-ember-subtle text-ember">
                  <Loader2 size={32} className="animate-spin" />
                </span>
                <h1 className="mt-5 font-display text-3xl font-extrabold tracking-tight text-coal" data-testid="donation-pending-heading">
                  Confirming your donation
                </h1>
                <p className="mt-3 text-sm leading-relaxed text-coal-muted">
                  We are waiting for PayFast to confirm your payment. This usually takes a few
                  seconds, feel free to keep this page open.
                </p>
              </>
            ) : (
              <>
                <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-ember-subtle text-ember">
                  <Heart size={28} />
                </span>
                <h1 className="mt-5 font-display text-3xl font-extrabold tracking-tight text-coal" data-testid="donation-unknown-heading">
                  We could not find that donation
                </h1>
                <p className="mt-3 text-sm leading-relaxed text-coal-muted">
                  If you completed a payment, it may still be processing. Check back shortly or get
                  in touch with the team.
                </p>
              </>
            )}
            <div className="mt-7 flex justify-center gap-3">
              <Link to="/" data-testid="donation-home-link" className="rounded-full bg-coal px-6 py-2.5 font-heading text-sm font-bold text-sand transition-colors hover:bg-teal">
                Back home
              </Link>
              <Link to="/events" data-testid="donation-events-link" className="rounded-full border border-coal/15 px-6 py-2.5 font-heading text-sm font-bold text-coal transition-colors hover:border-teal hover:text-teal">
                See the moments you fund
              </Link>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
